import { employerHubApi } from 'src/api';
import { AxiosRequestConfig } from 'axios';

import { TanstackTableSearchParamsAdapter } from '@itm/shared-frontend/lib/utils';

import {
  CreatePayrollModel,
  NextPayrollPeriodResponse,
  PayrollResponse,
  UpdatePayrollModel,
  DisablePayrollDto,
  ShortUpdatePayrollDto,
  AccessRequestModel,
  ShortPayrollEmployerResponse,
  DirectDebitStatus,
  PayrollUserResponsePagedResponse,
  SearchPayrollResponsePagedResponse,
} from 'src/types';

export const getNextPayrollByEmployerId = (companyId: string, employerId: string, config: AxiosRequestConfig = {}) =>
  employerHubApi.get<NextPayrollPeriodResponse[]>(
    `api/v1/companies/${companyId}/employers/${employerId}/payrolls/next`,
    config,
  );

export type PayrollByEmployerIdSearchParams = TanstackTableSearchParamsAdapter & {
  includeInactive?: string;
};

export const getPayrollByEmployerId = (
  companyId: string,
  employerId: string,
  config: AxiosRequestConfig = {},
  params?: PayrollByEmployerIdSearchParams,
) =>
  employerHubApi.get<PayrollResponse[]>(`api/v1/companies/${companyId}/employers/${employerId}/payrolls`, {
    ...config,
    params,
  });

export const createPayroll = (companyId: string, data: CreatePayrollModel, config: AxiosRequestConfig = {}) =>
  employerHubApi.post<string>(`api/v1/companies/${companyId}/payrolls`, data, config);

export const getPayrollByPayrollId = (companyId: string, payrollId: string, config: AxiosRequestConfig = {}) =>
  employerHubApi.get<PayrollResponse>(`api/v1/companies/${companyId}/payrolls/${payrollId}`, config);

export const updatePayroll = (companyId: string, data: UpdatePayrollModel) =>
  employerHubApi.patch<void>(`api/v1/companies/${companyId}/payrolls`, data);

export const disablePayroll = (companyId: string, data: DisablePayrollDto) =>
  employerHubApi.patch<void>(`api/v1/companies/${companyId}/payrolls/disable`, data);

export type PayrollListSearchParams = TanstackTableSearchParamsAdapter & {
  payrollStatus?: string;
  frequencyType?: string;
  directDebitStatuses?: DirectDebitStatus[];
  schemeId?: string;
};

export const getPayrollListSearch = (
  companyId: string,
  params: PayrollListSearchParams,
  config: AxiosRequestConfig = {},
) =>
  employerHubApi.get<SearchPayrollResponsePagedResponse>(`/api/v1/companies/${companyId}/payrolls/search`, {
    ...config,
    params,
  });

export const enablePayrollRestriction = (companyId: string, data: ShortUpdatePayrollDto) =>
  employerHubApi.patch<void>(`/api/v1/companies/${companyId}/payrolls/enable-payroll-restriction`, data);

export const disablePayrollRestriction = (companyId: string, data: ShortUpdatePayrollDto) =>
  employerHubApi.patch<void>(`/api/v1/companies/${companyId}/payrolls/disable-payroll-restriction`, data);

export type PayrollUsersSearchParams = TanstackTableSearchParamsAdapter & {
  employerId: string;
  payrollId: string;
};

export const getPayrollUsersWithAccessSearch = (
  companyId: string,
  params: PayrollUsersSearchParams,
  config: AxiosRequestConfig = {},
) =>
  employerHubApi.get<PayrollUserResponsePagedResponse>(
    `/api/v1/companies/${companyId}/payrolls/search-payroll-users-with-access`,
    {
      ...config,
      params,
    },
  );

export const getPayrollUsersSearch = (
  companyId: string,
  params: PayrollUsersSearchParams,
  config: AxiosRequestConfig = {},
) =>
  employerHubApi.get<PayrollUserResponsePagedResponse>(`/api/v1/companies/${companyId}/payrolls/search-payroll-users`, {
    ...config,
    params,
  });

export const grantPayrollAccess = (companyId: string, data: AccessRequestModel) =>
  employerHubApi.patch<void>(`/api/v1/companies/${companyId}/payrolls/grant-access`, data);

export const revokePayrollAccess = (companyId: string, data: AccessRequestModel) =>
  employerHubApi.patch<void>(`/api/v1/companies/${companyId}/payrolls/revoke-access`, data);

export const getShortPayrollEmployerDetails = (
  companyId: string,
  employerId: string,
  payrollId: string,
  config: AxiosRequestConfig = {},
) =>
  employerHubApi.get<ShortPayrollEmployerResponse>(
    `/api/v1/companies/${companyId}/employers/${employerId}/payroll/${payrollId}`,
    config,
  );

export const getManualDDI = (companyId: string, payrollId: string) =>
  employerHubApi.get<Blob>(`/api/v1/companies/${companyId}/manual/${payrollId}`, {
    responseType: 'blob',
  });
